import { ApiError } from './api';
import { createKey, signWithKey } from './webauthn';

// The second step of a sign-in (WRMM-134). Plain fetches, not the API client: a refusal here is a sign-in failure, not
// a session ending. The server holds the pending sign-in in an HttpOnly cookie; nothing here sees it.
export interface PendingSignIn { keys: number; authenticator: boolean; recoveryCodes: number; enrol: boolean }
export interface NewAuthenticator { secret: string; uri: string }

async function call<T>(path: string, body?: unknown, method: 'GET' | 'POST' = 'POST'): Promise<T> {
  const response = await fetch(path, { method, credentials: 'same-origin', cache: 'no-store', redirect: 'error',
    headers: body === undefined ? {} : { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
  if (!response.ok) throw new ApiError(response.status);
  return (response.status === 204 ? undefined : await response.json()) as T;
}

/** The sign-in waiting for its second factor, or null when there is none (or it expired). */
export async function getPendingSignIn(signal?: AbortSignal): Promise<PendingSignIn | null> {
  const response = await fetch('/auth/pending', { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal });
  if (response.status === 401) return null;
  if (!response.ok) throw new ApiError(response.status);
  return response.json();
}

export const finishWithCode = (code: string) => call<void>('/auth/pending/code', { code });
export async function finishWithKey() {
  const options = await call<Record<string, unknown>>('/auth/pending/key/options');
  await call<void>('/auth/pending/key', await signWithKey(options));
}
export const finishSynthetic = () => call<void>('/auth/pending/synthetic');
/** Ends the pending sign-in (the person chose to start over). */
export const cancelSignIn = () => call<void>('/auth/pending/cancel');

export const enrolAuthenticator = () => call<NewAuthenticator>('/auth/pending/enrol/authenticator');
export const confirmAuthenticator = (code: string) => call<{ recoveryCodes: string[] }>('/auth/pending/enrol/authenticator/confirm', { code });
export async function enrolKey() {
  const options = await call<Record<string, unknown>>('/auth/pending/enrol/key/options');
  return call<{ recoveryCodes: string[] }>('/auth/pending/enrol/key', { label: `Passkey added ${new Date().toLocaleDateString()}`, response: await createKey(options) });
}
